import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SortSelect } from '../components/SortSelect';
import { FilterPanel } from '../components/FilterPanel';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ExportDialog } from '../components/ExportDialog';
import { useExport } from '../hooks/useExport';
import { exportTableToPDF } from '../utils/html2pdfExport';
import Pagination from '../components/Pagination';
import { useServerList } from '../hooks/useServerList';
import { tesisTuruText, tesisTuruList, tesisTuruOptionsFrom } from '../utils/tesisTuru';
import { DTDetailDialog } from '../components/modules/bakim-onarim/DTDetailDialog';
import { exportDTDetayPDF } from '../utils/bakimDetayPdf';
import * as XLSX from 'xlsx';

import { DT_EXPORT_COLUMNS, INITIAL_DT_FORM_DATA, INITIAL_DT_FILTERS } from '../components/modules/dogrudan-temin/constants';
import { DTFormDialog } from '../components/modules/dogrudan-temin/DTFormDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DT_SORT_MAP = {
  newest: 'created_at:desc',
  oldest: 'created_at:asc',
  updated: 'updated_at:desc',
  name_asc: 'isin_adi:asc',
  name_desc: 'isin_adi:desc',
  amount_high: 'sozlesme_bedeli:desc',
  amount_low: 'sozlesme_bedeli:asc',
};

const DogrudenTeminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [odenekRecords, setOdenekRecords] = useState([]);
  const [usedOdenekIdsServer, setUsedOdenekIdsServer] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [sortKey, setSortKey] = useState('newest');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfDialogOpen, setSelectedPdfDialogOpen] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState('filtered'); // 'page' | 'filtered'
  const { selectedColumns, setSelectedColumns, pdfPageSize, setPdfPageSize, toggleColumn, selectAll, clearAll } = useExport();
  const [users, setUsers] = useState([]);
  const [detailRecord, setDetailRecord] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [wizardMode, setWizardMode] = useState(false);
  const [wizardOdenek, setWizardOdenek] = useState(null);
  const [firmaList, setFirmaList] = useState([]);
  const [formData, setFormData] = useState({...INITIAL_DT_FORM_DATA});
  const [uiFilters, setUiFilters] = useState({...INITIAL_DT_FILTERS});

  // Server-side hook
  const list = useServerList('/bakim-onarim/dogrudan-temin', {
    pageSize: 100,
    initialSort: 'created_at:desc',
  });

  const records = list.items;
  const loading = list.loading;
  const filteredRecords = list.items; // server already filtered

  // Filtre senkronizasyonu (UI → server)
  useEffect(() => {
    const f = {};
    ['yil', 'tesis_turu', 'ilce', 'ilgilenen_memur', 'talep_yeri', 'alim_turu', 'isi_yapan_firma'].forEach(k => {
      if (Array.isArray(uiFilters[k]) && uiFilters[k].length > 0) f[k] = uiFilters[k];
    });
    if (Array.isArray(uiFilters.odenekten_gelen) && uiFilters.odenekten_gelen.length > 0) {
      const arr = uiFilters.odenekten_gelen;
      if (arr.includes('evet') && !arr.includes('hayir')) f.odenekten_gelen = 'true';
      else if (arr.includes('hayir') && !arr.includes('evet')) f.odenekten_gelen = 'false';
    }
    // text aramaları birleştir: search = isin_adi + tesis_adi (regex)
    const text = [uiFilters.isin_adi, uiFilters.tesis_adi].filter(Boolean).join(' ');
    list.setSearch(text || '');
    list.setFilters(f);
  }, [JSON.stringify(uiFilters)]);

  // Sort senkronizasyonu
  useEffect(() => {
    list.setSort(DT_SORT_MAP[sortKey] || 'created_at:desc');
  }, [sortKey]);

  useEffect(() => {
    if (!user?.is_admin && !user?.has_bakim_onarim_access) {
      navigate('/dashboard');
      return;
    }
    fetchOdenekRecords();
    fetchUsers();
    fetchFirmaList();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users-simple`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchRecords = () => list.refetch();

  // Filtre dropdown'ları için seçenekler (server-side: mevcut sayfa içeriğinden + bilinen değerler)
  const yearOptions = useMemo(() => {
    const set = new Set();
    records.forEach(r => r.yil && set.add(String(r.yil)));
    // Son 10 yıl da ekle
    const cy = new Date().getFullYear();
    for (let y = cy + 1; y >= cy - 9; y--) set.add(String(y));
    return [...set].sort((a, b) => Number(b) - Number(a)).map(y => ({ v: y, l: y }));
  }, [records]);
  const tesisTuruOptions = useMemo(() => tesisTuruOptionsFrom(records), [records]);
  const ilceOptions = useMemo(() => [...new Set(records.map(r => r.ilce).filter(Boolean))].sort().map(i => ({ v: i, l: i })), [records]);
  const memurOptions = useMemo(() => [...new Set(records.map(r => r.ilgilenen_memur).filter(Boolean))].sort().map(m => ({ v: m, l: m })), [records]);
  const talepYeriOptions = useMemo(() => [...new Set(records.map(r => r.talep_yeri).filter(Boolean))].sort().map(t => ({ v: t, l: t })), [records]);
  const alimTuruOptions = useMemo(() => [...new Set(records.map(r => r.alim_turu).filter(Boolean))].sort().map(a => ({ v: a, l: a })), [records]);
  const firmaOptions = useMemo(() => [...new Set(records.map(r => r.isi_yapan_firma).filter(Boolean))].sort().map(f => ({ v: f, l: f })), [records]);

  const fetchOdenekRecords = async () => {
    try {
      const [odenekRes, usedRes] = await Promise.all([
        axios.get(`${API}/bakim-onarim/odenek`, { withCredentials: true }),
        axios.get(`${API}/bakim-onarim/dogrudan-temin/used-odenek-ids`, { withCredentials: true }),
      ]);
      const geldiOdendi = (odenekRes.data || []).filter(
        r => r.odenek_durumu === 'GELDİ-ÖDENDİ' && !r.dt_dismissed
      );
      setOdenekRecords(geldiOdendi);
      setUsedOdenekIdsServer(new Set(usedRes.data?.ids || []));
    } catch (error) {
      console.error('Odenek fetch error:', error);
    }
  };

  const fetchFirmaList = async () => {
    try {
      const response = await axios.get(`${API}/bakim-onarim/firma-simple`, { withCredentials: true });
      setFirmaList(response.data || []);
    } catch (error) {
      console.error('Firma list fetch error:', error);
    }
  };

  const displayMoney = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/[^0-9,]/g, '');
    if (!cleaned) return '';
    const parts = cleaned.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const result = decimalPart !== undefined ? `${formatted},${decimalPart.substring(0, 2)}` : formatted;
    return result ? result + ' TL' : '';
  };


  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`${API}/bakim-onarim/dogrudan-temin/${selectedRecord.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/bakim-onarim/dogrudan-temin`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      resetForm();
      fetchRecords();
      fetchOdenekRecords();
    } catch (error) {
      console.error('Submit error:', error);
      if (error.response?.status === 409) {
        alert('⚠️ ' + (error.response.data?.detail || 'Bu ödenek için doğrudan temin zaten başlatılmış'));
        setDialogOpen(false);
        resetForm();
        fetchRecords();
        fetchOdenekRecords();
      } else {
        alert('İşlem sırasında hata oluştu: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    
    // Migration: Eski butce_tertibi field'ını yeni list yapısına dönüştür
    let butceTertibiList = record.butce_tertibi_list || [];
    if (butceTertibiList.length === 0 && record.butce_tertibi) {
      butceTertibiList = [record.butce_tertibi];
    }
    
    setFormData({
      yil: record.yil || new Date().getFullYear().toString(),
      ilgilenen_memur: record.ilgilenen_memur || '',
      piyasa_arastirma_gorevlileri: record.piyasa_arastirma_gorevlileri || [],
      ilce: record.ilce || '',
      tesis_turu: tesisTuruList(record.tesis_turu),
      tesis_adi: record.tesis_adi || '',
      isin_adi: record.isin_adi || '',
      alim_turu: record.alim_turu || '',
      firma1_adi: record.firma1_adi || '',
      firma1_teklif: record.firma1_teklif || '',
      firma2_adi: record.firma2_adi || '',
      firma2_teklif: record.firma2_teklif || '',
      firma3_adi: record.firma3_adi || '',
      firma3_teklif: record.firma3_teklif || '',
      firma4_adi: record.firma4_adi || '',
      firma4_teklif: record.firma4_teklif || '',
      isi_yapan_firma: record.isi_yapan_firma || '',
      sozlesme_bedeli: record.sozlesme_bedeli || '',
      isin_suresi: record.isin_suresi || '',
      odeme_tarihi: record.odeme_tarihi || '',
      odeme_belge_no: record.odeme_belge_no || '',
      odenen_tutar: record.odenen_tutar || '',
      butce_tertibi: record.butce_tertibi || '',
      butce_tertibi_list: butceTertibiList,
      odenek_id: record.odenek_id || '',
      odenek_tutari: record.odenek_tutari || '',
      talep_yeri: record.talep_yeri || ''
    });
    setEditMode(true);
    setWizardMode(false);
    setWizardOdenek(null);
    setCurrentStep(1);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/bakim-onarim/dogrudan-temin/${id}`, { withCredentials: true });
      fetchRecords();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Silme işlemi başarısız');
    }
  };

  // Bütçe Tertibi yönetimi
  const handleExcelUpload = async () => {
    if (!uploadFile) {
      alert('❌ Lütfen bir Excel dosyası seçin');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(`${API}/bakim-onarim/dogrudan-temin/upload-excel`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}\n${response.data.inserted_count} kayıt eklendi.`);
        setUploadDialogOpen(false);
        setUploadFile(null);
        fetchRecords(); // Refresh table
      } else {
        alert('⚠️ ' + response.data.message);
      }
    } catch (error) {
      console.error('Excel upload error:', error);
      alert('❌ Excel yükleme hatası: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };


  // Bulk Delete Handler
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('❌ Lütfen silinecek kayıtları seçin');
      return;
    }
    
    const confirmMessage = `⚠️ ${selectedRows.length} kayıt silinecek. Bu işlem geri alınamaz!\n\nEmin misiniz?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/bakim-onarim/dogrudan-temin/bulk`, {
        data: selectedRows,
        withCredentials: true
      });
      
      if (response.data.success) {
        alert(`✅ ${response.data.deleted_count} kayıt başarıyla silindi!`);
        setSelectedRows([]);
        fetchRecords();
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('❌ Silme hatası: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Bütçe Tertibi yönetimi
  const addButceTertibi = () => {
    setFormData({ ...formData, butce_tertibi_list: [...(formData.butce_tertibi_list || []), ''] });
  };

  const removeButceTertibi = (index) => {
    setFormData({ ...formData, butce_tertibi_list: (formData.butce_tertibi_list || []).filter((_, i) => i !== index) });
  };

  const updateButceTertibi = (index, value) => {
    const newList = [...(formData.butce_tertibi_list || [])];
    newList[index] = value;
    setFormData({ ...formData, butce_tertibi_list: newList });
  };




  const resetForm = () => {
    setFormData({...INITIAL_DT_FORM_DATA});
    setEditMode(false);
    setSelectedRecord(null);
    setCurrentStep(1);
    setWizardMode(false);
    setWizardOdenek(null);
  };

  const startWizardFromOdenek = (odenekRecord) => {
    const butceList = odenekRecord.butce_tertibi ? [odenekRecord.butce_tertibi] : [];
    setFormData({
      yil: odenekRecord.yil || new Date().getFullYear().toString(),
      ilgilenen_memur: odenekRecord.ilgilenen_memur || '',
      piyasa_arastirma_gorevlileri: [],
      ilce: odenekRecord.ilce || '',
      tesis_turu: tesisTuruList(odenekRecord.tesis_turu),
      tesis_adi: odenekRecord.tesis_adi || '',
      isin_adi: odenekRecord.isin_adi || '',
      alim_turu: odenekRecord.alim_turu || '',
  // Excel Upload Handler
      firma1_adi: '',
      firma1_teklif: '',
      firma2_adi: '',
      firma2_teklif: '',
      firma3_adi: '',
      firma3_teklif: '',
      firma4_adi: '',
      firma4_teklif: '',
      isi_yapan_firma: '',
      sozlesme_bedeli: '',
      isin_suresi: '',
      odeme_tarihi: '',
      odeme_belge_no: '',
      odenen_tutar: '',
      butce_tertibi: odenekRecord.butce_tertibi || '',
      butce_tertibi_list: butceList,
      odenek_id: odenekRecord.id,
      odenek_tutari: odenekRecord.odenek_tutari || '',
      talep_yeri: odenekRecord.talep_yeri || ''
    });
    setWizardMode(true);
    setWizardOdenek(odenekRecord);
    setEditMode(false);
    setSelectedRecord(null);
    setCurrentStep(1);
    setDialogOpen(true);
  };

  // Filtreleme: server tarafında uygulandığı için lokal filtre yok
  // filteredRecords = list.items (sunucu zaten filtreliyor)

  // Zaten doğrudan temine aktarılmış ödenekler: sunucudaki TÜM kayıtlara göre (sayfalamadan bağımsız)
  const pageUsedIds = records.filter(r => r.odenek_id).map(r => r.odenek_id);
  const tumKullanilmisOdenekIds = new Set([...usedOdenekIdsServer, ...pageUsedIds]);
  const availableOdenekRecords = odenekRecords.filter(r => !tumKullanilmisOdenekIds.has(r.id));

  // Admin: talebi listeden kaldır (Ödenek sayfasındaki kayıt silinmez)
  const dismissOdenekTalep = async (odenekRec) => {
    if (!window.confirm(`"${odenekRec.isin_adi}" talebi bu listeden kaldırılacak.\n\nNot: Ödenek sayfasındaki asıl kayıt SİLİNMEZ, yalnızca "Ödenekten Gelen Talepler" listesinden çıkarılır. Emin misiniz?`)) return;
    try {
      await axios.put(`${API}/bakim-onarim/odenek/${odenekRec.id}/dt-dismiss`, {}, { withCredentials: true });
      fetchOdenekRecords();
    } catch (e) {
      alert('Silinemedi: ' + (e.response?.data?.detail || e.message));
    }
  };


  // Satır seçme fonksiyonları
  const toggleRowSelection = (recordId) => {
    if (selectedRows.includes(recordId)) {
      setSelectedRows(selectedRows.filter(id => id !== recordId));
    } else {
      setSelectedRows([...selectedRows, recordId]);
    }
  };

  const toggleAllRows = () => {
    if (selectedRows.length === filteredRecords.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredRecords.map(r => r.id));
    }
  };

  // Finansal özet hesaplama
  const getSelectedRecordsSummary = () => {
    const selectedRecordsData = filteredRecords.filter(r => selectedRows.includes(r.id));
    
    const totalOdenek = selectedRecordsData.reduce((sum, record) => {
      const tutar = parseFloat((record.sozlesme_bedeli || '0').replace(/[^0-9,]/g, '').replace(',', '.'));
      return sum + (isNaN(tutar) ? 0 : tutar);
    }, 0);

    return {
      count: selectedRecordsData.length,
      totalOdenek: totalOdenek.toFixed(2).replace('.', ','),
      records: selectedRecordsData
    };
  };

  // Seçili kayıtları PDF olarak export et
  const exportSelectedToPDF = () => {
    const summary = getSelectedRecordsSummary();
    if (summary.count === 0) {
      alert('Lütfen en az bir kayıt seçin');
      return;
    }
    setSelectedPdfDialogOpen(true);
  };

  const exportSelectedToPDFWithColumns = async () => {
    const summary = getSelectedRecordsSummary();
    if (selectedColumns.length === 0) {
      alert('Lütfen en az bir sütun seçin');
      return;
    }

    const columnMapping = {
      'yil': 'Yıl', 'isin_adi': 'İşin Adı', 'tesis_turu': 'Tesis Türü',
      'tesis_adi': 'Tesis Adı', 'ilce': 'İlçe', 'talep_yeri': 'Talep Yeri',
      'butce_tertibi': 'Bütçe Tertibi', 'sozlesme_bedeli': 'Sözleşme Bedeli',
      'ilgilenen_memur': 'İlgilenen Memur', 'alim_turu': 'Alım Türü',
      'firma1_adi': '1. Firma Adı', 'firma1_teklif': '1. Firma Teklif',
      'firma2_adi': '2. Firma Adı', 'firma2_teklif': '2. Firma Teklif',
      'firma3_adi': '3. Firma Adı', 'firma3_teklif': '3. Firma Teklif',
      'firma4_adi': '4. Firma Adı', 'firma4_teklif': '4. Firma Teklif',
      'isi_yapan_firma': 'İşi Yapan Firma', 'isin_suresi': 'İşin Süresi',
      'odeme_tarihi': 'Ödeme Tarihi', 'odeme_belge_no': 'Ödeme Belge No',
      'odenen_tutar': 'Ödenen Tutar', 'odenek_tutari': 'Ödenek Tutarı',
      'piyasa_arastirma_gorevlileri': 'Piy. Araş. Görevlileri',
      'odenek_talep_tarihi': 'Ödenek Talep Tarihi', 'talep_yazi_no': 'Talep Yazı No',
      'odenek_durumu': 'Ödenek Durumu', 'aciklama': 'Açıklama'
    };

    const headers = ['S.NO', ...selectedColumns.map(col => columnMapping[col] || col)];
    const tableData = summary.records.map((record, idx) => [
      idx + 1,
      ...selectedColumns.map(col => {
        if (['sozlesme_bedeli', 'odenen_tutar', 'odenek_tutari', 'firma1_teklif', 'firma2_teklif', 'firma3_teklif', 'firma4_teklif'].includes(col)) return displayMoney(record[col]);
        if (col === 'piyasa_arastirma_gorevlileri') return (record[col] || []).join(', ');
        if (col === 'butce_tertibi') return (record.butce_tertibi_list || []).join(', ') || record.butce_tertibi || '';
        if (col === 'tesis_turu') return tesisTuruText(record[col]);
        return record[col] || '';
      })
    ]);

    const totalRow = ['TOPLAM'];
    selectedColumns.forEach(col => {
      if (col === 'sozlesme_bedeli') {
        const total = summary.records.reduce((sum, record) => {
          const tutar = parseFloat((record.odenek_tutari || '0').replace(/[^0-9,]/g, '').replace(',', '.'));
          return sum + (isNaN(tutar) ? 0 : tutar);
        }, 0);
        totalRow.push(displayMoney(total.toFixed(2).replace('.', ',')));
      } else {
        totalRow.push('');
      }
    });

    await exportTableToPDF(headers, [...tableData, totalRow], 'Doğrudan Temin Seçili Kayıtlar', 'dogrudan-temin-secili-kayitlar.pdf', {
      subtitle: `Seçili: ${summary.count} | Toplam: ${displayMoney(summary.totalOdenek)}`,
      hasTotalRow: true,
      pageSize: pdfPageSize
    });
    setSelectedPdfDialogOpen(false);
  };

  const exportToExcel = async () => {
    if (selectedColumns.length === 0) {
      alert('Lütfen en az bir sütun seçin');
      return;
    }

    const sourceRecords = exportScope === 'filtered' ? await list.fetchAll() : filteredRecords;
    const dataToExport = sourceRecords.map(record => {
      const row = {};
      selectedColumns.forEach(col => {
        const columnNames = {
          'yil': 'YIL',
          'ilgilenen_memur': 'İLGİLENEN MEMUR',
          'piyasa_arastirma_gorevlileri': 'PİYASA ARAŞTIRMA GÖREVLİLERİ',
          'ilce': 'İLÇE',
          'tesis_turu': 'TESİS TÜRÜ',
          'tesis_adi': 'TESİS ADI',
          'isin_adi': 'İŞİN ADI',
          'alim_turu': 'ALIM TÜRÜ',
          'talep_yeri': 'TALEP YERİ',
          'odenek_tutari': 'ÖDENEK TUTARI',
          'firma1_adi': '1. FİRMA ADI',
          'firma1_teklif': '1. FİRMA TEKLİF',
          'firma2_adi': '2. FİRMA ADI',
          'firma2_teklif': '2. FİRMA TEKLİF',
          'firma3_adi': '3. FİRMA ADI',
          'firma3_teklif': '3. FİRMA TEKLİF',
          'firma4_adi': '4. FİRMA ADI',
          'firma4_teklif': '4. FİRMA TEKLİF',
          'isi_yapan_firma': 'İŞİ YAPAN FİRMA',
          'sozlesme_bedeli': 'SÖZLEŞME BEDELİ (KDV HARİÇ)',
          'isin_suresi': 'İŞİN SÜRESİ',
          'odeme_tarihi': 'ÖDEME TARİHİ',
          'odeme_belge_no': 'ÖDEME BELGE NO',
          'odenen_tutar': 'ÖDENEN TUTAR',
          'butce_tertibi': 'BÜTÇE TERTİBİ'
        };
        const val = record[col];
        if (col === 'piyasa_arastirma_gorevlileri') row[columnNames[col] || col] = (val || []).join(', ');
        else if (col === 'butce_tertibi') row[columnNames[col] || col] = (record.butce_tertibi_list || []).join(', ') || val || '';
        else if (col === 'tesis_turu') row[columnNames[col] || col] = tesisTuruText(val);
        else row[columnNames[col] || col] = val || '';
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Doğrudan Temin İşleri');
    XLSX.writeFile(wb, 'dogrudan-temin-isleri.xlsx');
    setExcelDialogOpen(false);
  };

  const exportSelectedToExcel = () => {
    const summary = getSelectedRecordsSummary();
    if (summary.count === 0) {
      alert('Lütfen en az bir kayıt seçin');
      return;
    }

    const dataToExport = summary.records.map(record => ({
      'YIL': record.yil || '',
      'İLGİLENEN MEMUR': record.ilgilenen_memur || '',
      'PİYASA ARAŞTIRMA GÖREVLİLERİ': (record.piyasa_arastirma_gorevlileri || []).join(', '),
      'İLÇE': record.ilce || '',
      'TESİS TÜRÜ': tesisTuruText(record.tesis_turu),
      'İŞİN ADI': record.isin_adi || '',
      '1. FİRMA ADI': record.firma1_adi || '',
      '1. FİRMA TEKLİF': record.firma1_teklif || '',
      '2. FİRMA ADI': record.firma2_adi || '',
      '2. FİRMA TEKLİF': record.firma2_teklif || '',
      '3. FİRMA ADI': record.firma3_adi || '',
      '3. FİRMA TEKLİF': record.firma3_teklif || '',
      '4. FİRMA ADI': record.firma4_adi || '',
      '4. FİRMA TEKLİF': record.firma4_teklif || '',
      'İŞİ YAPAN FİRMA': record.isi_yapan_firma || '',
      'SÖZLEŞME BEDELİ (KDV HARİÇ)': record.sozlesme_bedeli || '',
      'İŞİN SÜRESİ': record.isin_suresi || '',
      'ÖDEME TARİHİ': record.odeme_tarihi || '',
      'ÖDEME BELGE NO': record.odeme_belge_no || '',
      'ÖDENEN TUTAR': record.odenen_tutar || '',
      'BÜTÇE TERTİBİ': record.butce_tertibi || ''
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Seçili Kayıtlar');
    XLSX.writeFile(wb, 'dogrudan-temin-secili.xlsx');
  };

  const exportToPDF = async () => {
    if (selectedColumns.length === 0) {
      alert('Lütfen en az bir sütun seçin');
      return;
    }

    const columnMapping = {
      'yil': 'Yıl', 'isin_adi': 'İşin Adı', 'tesis_turu': 'Tesis Türü',
      'tesis_adi': 'Tesis Adı', 'ilce': 'İlçe', 'talep_yeri': 'Talep Yeri',
      'butce_tertibi': 'Bütçe Tertibi', 'sozlesme_bedeli': 'Sözleşme Bedeli',
      'ilgilenen_memur': 'İlgilenen Memur', 'alim_turu': 'Alım Türü',
      'firma1_adi': '1. Firma Adı', 'firma1_teklif': '1. Firma Teklif',
      'firma2_adi': '2. Firma Adı', 'firma2_teklif': '2. Firma Teklif',
      'firma3_adi': '3. Firma Adı', 'firma3_teklif': '3. Firma Teklif',
      'firma4_adi': '4. Firma Adı', 'firma4_teklif': '4. Firma Teklif',
      'isi_yapan_firma': 'İşi Yapan Firma', 'isin_suresi': 'İşin Süresi',
      'odeme_tarihi': 'Ödeme Tarihi', 'odeme_belge_no': 'Ödeme Belge No',
      'odenen_tutar': 'Ödenen Tutar', 'odenek_tutari': 'Ödenek Tutarı',
      'piyasa_arastirma_gorevlileri': 'Piy. Araş. Görevlileri',
      'odenek_talep_tarihi': 'Ödenek Talep Tarihi', 'talep_yazi_no': 'Talep Yazı No',
      'odenek_durumu': 'Ödenek Durumu', 'aciklama': 'Açıklama'
    };

    const headers = selectedColumns.map(col => columnMapping[col] || col);
    const sourceRecords = exportScope === 'filtered' ? await list.fetchAll() : filteredRecords;
    const tableData = sourceRecords.map(record =>
      selectedColumns.map(col => {
        if (['sozlesme_bedeli', 'odenen_tutar', 'odenek_tutari', 'firma1_teklif', 'firma2_teklif', 'firma3_teklif', 'firma4_teklif'].includes(col)) return displayMoney(record[col]);
        if (col === 'piyasa_arastirma_gorevlileri') return (record[col] || []).join(', ');
        if (col === 'butce_tertibi') return (record.butce_tertibi_list || []).join(', ') || record.butce_tertibi || '';
        if (col === 'tesis_turu') return tesisTuruText(record[col]);
        return record[col] || '';
      })
    );

    const totalRow = selectedColumns.map((col, colIdx) => {
      if (colIdx === 0) return 'TOPLAM';
      if (col === 'sozlesme_bedeli') {
        const total = sourceRecords.reduce((sum, record) => {
          const tutar = parseFloat((record.odenek_tutari || '0').replace(/[^0-9,]/g, '').replace(',', '.'));
          return sum + (isNaN(tutar) ? 0 : tutar);
        }, 0);
        return displayMoney(total.toFixed(2).replace('.', ','));
      }
      return '';
    });

    await exportTableToPDF(headers, [...tableData, totalRow], 'Doğrudan Temin Ödenek Talepleri', 'dogrudan-temin-odenek.pdf', {
      hasTotalRow: true,
      pageSize: pdfPageSize
    });
    setPdfDialogOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Doğrudan Temin</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/bakim-onarim/dogrudan-temin')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors border-b-2 border-blue-500"
              >
                🔧 Doğrudan Temin İşleri
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/bakim-onarim/firma-bilgileri')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Firma Bilgileri
              </button>
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div 
                  className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">
                      {user?.is_admin ? 'Admin' : 'Kullanıcı'}
                    </p>
                  </div>
                </div>
                <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">
                  Çıkış
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">🔧 Doğrudan Temin İşleri Talepleri</h2>
            <p className="text-gray-400">Ödenek taleplerini görüntüleyin ve yönetin</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setExcelDialogOpen(true)} className="bg-green-600 hover:bg-green-700 text-white">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel İndir
            </Button>
            <Button onClick={() => setPdfDialogOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              PDF İndir
            </Button>
            {!user?.is_viewer && (
              <Button 
                onClick={() => {
                  resetForm();
                  setCurrentStep(1);
                  setDialogOpen(true);
                }} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="new-record-btn"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Yeni İş Ekle
              </Button>
            )}
          </div>
        </div>


        {/* Filtreleme */}
        <FilterPanel
          filters={uiFilters}
          setFilters={setUiFilters}
          totalCount={list.total}
          filteredCount={list.total}
          resetFilters={() => setUiFilters({...INITIAL_DT_FILTERS})}
          filterConfig={[
            { key: 'isin_adi', label: 'Is Adi', type: 'text' },
            { key: 'tesis_adi', label: 'Tesis Adi', type: 'text' },
            { key: 'yil', label: 'Yil', type: 'multi', options: yearOptions },
            { key: 'tesis_turu', label: 'Tesis Türü', type: 'multi', options: tesisTuruOptions },
            { key: 'ilce', label: 'Ilce', type: 'multi', options: ilceOptions },
            { key: 'ilgilenen_memur', label: 'Ilgilenen Memur', type: 'multi', options: memurOptions },
            { key: 'talep_yeri', label: 'Talep Yeri', type: 'multi', options: talepYeriOptions },
            { key: 'alim_turu', label: 'Alim Türü', type: 'multi', options: alimTuruOptions },
            { key: 'isi_yapan_firma', label: 'Isi Yapan Firma', type: 'multi', options: firmaOptions },
            { key: 'odenekten_gelen', label: 'Ödenekten Gelen', type: 'multi', options: [{v:'evet',l:'Evet'},{v:'hayir',l:'Hayir'}] },
          ]}
        />


        {/* Ödenekten Gelen Talepler - GELDİ-ÖDENDİ */}
        {availableOdenekRecords.length > 0 && (
          <Card className="bg-gradient-to-br from-emerald-900/20 to-teal-900/20 border-emerald-700/50 mb-6" data-testid="odenek-geldi-odendi-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-emerald-400 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Ödenekten Gelen Talepler ({availableOdenekRecords.length})
                </CardTitle>
                <span className="text-xs text-emerald-400/70 bg-emerald-900/30 px-3 py-1 rounded-full">GELDİ-ÖDENDİ</span>
              </div>
              <p className="text-sm text-gray-400 mt-1">Ödenek durumu "GELDİ-ÖDENDİ" olan kayıtlar. Doğrudan temin sürecini başlatmak için bir kayıt seçin.</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-300">
                  <thead className="text-xs text-emerald-400/80 uppercase bg-emerald-900/20">
                    <tr>
                      <th className="px-4 py-3">İşin Adı</th>
                      <th className="px-4 py-3">Tesis Türü</th>
                      <th className="px-4 py-3">İlçe</th>
                      <th className="px-4 py-3">Ödenek Tutarı</th>
                      <th className="px-4 py-3">Talep Yeri</th>
                      <th className="px-4 py-3">İlgilenen Memur</th>
                      <th className="px-4 py-3">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableOdenekRecords.map((odenekRec) => (
                      <tr key={odenekRec.id} className="border-b border-emerald-800/30 hover:bg-emerald-900/20 transition-colors">
                        <td className="px-4 py-3 font-medium text-white max-w-xs truncate" title={odenekRec.isin_adi}>{odenekRec.isin_adi || '-'}</td>
                        <td className="px-4 py-3">{tesisTuruText(odenekRec.tesis_turu) || '-'}</td>
                        <td className="px-4 py-3">{odenekRec.ilce || '-'}</td>
                        <td className="px-4 py-3 text-emerald-400 font-semibold">{displayMoney(odenekRec.odenek_tutari)}</td>
                        <td className="px-4 py-3">{odenekRec.talep_yeri || '-'}</td>
                        <td className="px-4 py-3">{odenekRec.ilgilenen_memur || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {!user?.is_viewer && (
                              <Button
                                onClick={() => startWizardFromOdenek(odenekRec)}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`start-wizard-${odenekRec.id}`}
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                                D.T. Başlat
                              </Button>
                            )}
                            {user?.is_admin && (
                              <Button
                                onClick={() => dismissOdenekTalep(odenekRec)}
                                size="sm"
                                variant="outline"
                                className="border-red-700 text-red-400 hover:bg-red-900/30 hover:text-red-300"
                                data-testid={`dismiss-odenek-${odenekRec.id}`}
                              >
                                Sil
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Records Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">
              Doğrudan Temin İşleri ({list.total})
              {loading && <span className="ml-2 text-xs text-gray-500 animate-pulse">yükleniyor…</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {list.total === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">
                  {loading ? 'Yükleniyor...' : 'Filtreye uygun kayıt bulunamadı'}
                </p>
              </div>
            ) : (
              <>
              <div className="flex justify-end mb-2 px-2">
                <SortSelect value={sortKey} onChange={setSortKey} options={['newest','oldest','updated','name_asc','name_desc','amount_high','amount_low']} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-300">
                  <thead className="text-xs text-gray-400 uppercase bg-gray-800">
                    <tr>
                      <th className="px-4 py-3">
                        <Checkbox
                          checked={selectedRows.length === records.length && records.length > 0}
                          onCheckedChange={toggleAllRows}
                          className="border-gray-600"
                        />
                      </th>
                      <th className="px-4 py-3">S.NO</th>
                      <th className="px-4 py-3">Yıl</th>
                      <th className="px-4 py-3">İşin Adı</th>
                      <th className="px-4 py-3">Tesis Türü</th>
                      <th className="px-4 py-3">İlçe</th>
                      <th className="px-4 py-3">İlgilenen Memur</th>
                      <th className="px-4 py-3">Ödenek Tutarı</th>
                      <th className="px-4 py-3">Durum</th>
                      {user?.is_admin && <th className="px-4 py-3">Son Düzenleyen</th>}
                      <th className="px-4 py-3">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record, index) => {
                      const _start = (list.page - 1) * list.pageSize;
                      return (
                      <tr key={record.id} className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer" data-testid={`dt-row-${record.id}`}
                        onClick={(e) => { if (e.target.closest('button, input, a, [role="checkbox"]')) return; setDetailRecord(record); }}>
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selectedRows.includes(record.id)}
                            onCheckedChange={() => toggleRowSelection(record.id)}
                            className="border-gray-600"
                          />
                        </td>
                        <td className="px-4 py-3">{_start + index + 1}</td>
                        <td className="px-4 py-3">{record.yil || '-'}</td>
                        <td className="px-4 py-3 max-w-md truncate" title={record.isin_adi}>
                          {record.isin_adi || '-'}
                          {record.odenek_id && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-emerald-900/40 text-emerald-400 text-[10px] rounded" title="Ödenekten aktarıldı">Ödenek</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{tesisTuruText(record.tesis_turu) || '-'}</td>
                        <td className="px-4 py-3">{record.ilce || '-'}</td>
                        <td className="px-4 py-3">{record.ilgilenen_memur || '-'}</td>
                        <td className="px-4 py-3">{displayMoney(record.odenek_tutari)}</td>
                        <td className="px-4 py-3">{record.odenek_durumu || '-'}</td>
                        {user?.is_admin && (
                          <td className="px-4 py-3 text-xs text-gray-400">
                            {record.last_modified_by_name || '-'}
                            {record.last_modified_at && <div className="text-gray-500 mt-0.5">{new Date(record.last_modified_at).toLocaleString('tr-TR')}</div>}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {!user?.is_viewer && (
                              <>
                                <Button
                                  onClick={() => handleEdit(record)}
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-400 hover:text-blue-300"
                                >
                                  Düzenle
                                </Button>
                                {user?.is_admin && (
                                  <Button
                                    onClick={() => handleDelete(record.id)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    Sil
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
              {/* Server-side pagination */}
              {list.total > 0 && (
                <div className="border-t border-gray-700/40 px-3 py-2">
                  <Pagination
                    page={list.page}
                    totalPages={list.totalPages}
                    total={list.total}
                    pageSize={list.pageSize}
                    onPageChange={list.setPage}
                    onPageSizeChange={list.setPageSize}
                    loading={loading}
                    testIdPrefix="dogruden-temin-pagination"
                  />
                </div>
              )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Finansal Özet - Seçili Kayıtlar */}
        {selectedRows.length > 0 && (
          <Card className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-blue-700/50 mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-blue-400">📊 Finansal Özet - Seçili Kayıtlar</CardTitle>
                <div className="flex gap-2">
                  <Button
                    onClick={exportSelectedToExcel}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Seçilenleri Excel İndir
                  </Button>
                  <Button
                    onClick={exportSelectedToPDF}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Seçilenleri PDF İndir
                  </Button>
                  {user?.is_admin && (
                    <Button onClick={handleBulkDelete} variant="outline" className="bg-red-900/30 border-red-700 text-red-400 hover:bg-red-900/50">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      🗑️ Seçilileri Sil ({selectedRows.length})
                    </Button>
                  )}
                  <Button onClick={() => setSelectedRows([])} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">Seçimi Temizle</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Seçili Kayıt Sayısı</p>
                  <p className="text-3xl font-bold text-white">{getSelectedRecordsSummary().count}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Toplam Ödenek Tutarı</p>
                  <p className="text-3xl font-bold text-green-400">{displayMoney(getSelectedRecordsSummary().totalOdenek)}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-xs text-gray-400 mb-1">Ortalama Tutar</p>
                  <p className="text-3xl font-bold text-purple-400">
                    {displayMoney((parseFloat(getSelectedRecordsSummary().totalOdenek.replace(',', '.')) / getSelectedRecordsSummary().count).toFixed(2).replace('.', ','))}
                  </p>
                </div>
              </div>

              {/* Detaylı Liste */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Seçili Kayıtlar Detayı:</h4>
                <div className="space-y-2">
                  {getSelectedRecordsSummary().records.map((record, idx) => (
                    <div key={record.id} className="bg-gray-800/30 rounded p-3 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">{idx + 1}. {record.isin_adi}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {tesisTuruText(record.tesis_turu) || '-'} • {record.yil} • {record.ilce || 'İlçe yok'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-400">{displayMoney(record.odenek_tutari)}</p>
                        <p className="text-xs text-gray-400">{record.odenek_durumu}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <DTFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editMode={editMode}
        wizardMode={wizardMode}
        wizardOdenek={wizardOdenek}
        formData={formData}
        setFormData={setFormData}
        users={users}
        firmaList={firmaList}
        displayMoney={displayMoney}
        onSubmit={handleSubmit}
        submitting={submitting}
        onCancel={() => { setDialogOpen(false); resetForm(); }}
        addButceTertibi={addButceTertibi}
        removeButceTertibi={removeButceTertibi}
        updateButceTertibi={updateButceTertibi}
      />

      {/* Satıra tıklanınca açılan bilgi kartı */}
      <DTDetailDialog
        open={!!detailRecord}
        onOpenChange={(v) => { if (!v) setDetailRecord(null); }}
        record={detailRecord}
        displayMoney={displayMoney}
        canEdit={!user?.is_viewer}
        onEdit={(rec) => handleEdit(rec)}
        onExportPDF={(rec) => exportDTDetayPDF(rec, { displayMoney })}
      />

      {/* PDF Sütun Seçimi Dialog */}
      <ExportDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        columns={DT_EXPORT_COLUMNS}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll(DT_EXPORT_COLUMNS)}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportToPDF}
        title="PDF İçin Sütun Seçin"
        description={`Kapsam: ${exportScope === 'filtered' ? `Tüm filtrelenmiş (${list.total})` : `Görünen sayfa (${records.length})`}`}
        quickSelect={{
          label: 'Hızlı Seçim (7 Temel)',
          onSelect: () => setSelectedColumns(['yil', 'ilgilenen_memur', 'ilce', 'tesis_turu', 'isin_adi', 'isi_yapan_firma', 'sozlesme_bedeli'])
        }}
        idPrefix="dt-pdf"
      >
        <ScopeToggle scope={exportScope} setScope={setExportScope} page={records.length} total={list.total} idPrefix="dt-pdf" />
      </ExportDialog>

      {/* Excel Sütun Seçimi Dialog */}
      <ExportDialog
        open={excelDialogOpen}
        onOpenChange={setExcelDialogOpen}
        columns={DT_EXPORT_COLUMNS}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll(DT_EXPORT_COLUMNS)}
        onClearAll={clearAll}
        onExportExcel={exportToExcel}
        title="Excel İçin Sütun Seçin"
        description={`Kapsam: ${exportScope === 'filtered' ? `Tüm filtrelenmiş (${list.total})` : `Görünen sayfa (${records.length})`}`}
        quickSelect={{
          label: 'Hızlı Seçim (7 Temel)',
          onSelect: () => setSelectedColumns(['yil', 'ilgilenen_memur', 'ilce', 'tesis_turu', 'isin_adi', 'isi_yapan_firma', 'sozlesme_bedeli'])
        }}
        idPrefix="dt-excel"
      >
        <ScopeToggle scope={exportScope} setScope={setExportScope} page={records.length} total={list.total} idPrefix="dt-excel" />
      </ExportDialog>

      {/* Seçili Kayıtlar PDF Sütun Seçimi Dialog */}
      <ExportDialog
        open={selectedPdfDialogOpen}
        onOpenChange={setSelectedPdfDialogOpen}
        columns={DT_EXPORT_COLUMNS}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll(DT_EXPORT_COLUMNS)}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportSelectedToPDFWithColumns}
        title="Seçili Kayıtlar PDF - Sütun Seçin"
        description={`Seçili ${getSelectedRecordsSummary().count} kayıt için PDF raporunda görmek istediğiniz sütunları seçin`}
        quickSelect={{
          label: 'Hızlı Seçim (7 Temel)',
          onSelect: () => setSelectedColumns(['yil', 'ilgilenen_memur', 'ilce', 'tesis_turu', 'isin_adi', 'isi_yapan_firma', 'sozlesme_bedeli'])
        }}
        idPrefix="dt-selected-pdf"
      />

      {/* Excel Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>📊 Excel Dosyası Yükle</DialogTitle>
            <DialogDescription className="text-gray-400">
              Doğrudan Temin işlerini toplu olarak eklemek için Excel dosyası yükleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 space-y-4">
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="hidden"
                id="excel-upload"
              />
              <label htmlFor="excel-upload" className="cursor-pointer flex flex-col items-center">
                <svg className="w-12 h-12 text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-sm text-gray-400">
                  {uploadFile ? (
                    <span className="text-blue-400 font-medium">📄 {uploadFile.name}</span>
                  ) : (
                    <>
                      <span className="text-blue-400 font-medium">Dosya Seç</span>
                      <span className="block text-xs mt-1">(.xlsx, .xls)</span>
                    </>
                  )}
                </span>
              </label>
            </div>
            
            <div className="bg-yellow-900/20 border border-yellow-700/50 rounded p-3">
              <p className="text-xs text-yellow-300">⚠️ Excel formatı sistem formatı ile uyumlu olmalıdır.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => { setUploadDialogOpen(false); setUploadFile(null); }} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700" disabled={uploading}>İptal</Button>
            <Button onClick={handleExcelUpload} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={!uploadFile || uploading}>
              {uploading ? 'Yükleniyor...' : '📊 Yükle ve Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Export kapsam toggle bileşeni
const ScopeToggle = ({ scope, setScope, page, total, idPrefix }) => (
  <div className="flex items-center gap-2 mb-2 p-2 bg-gray-800/60 rounded-lg">
    <span className="text-xs text-gray-400 mr-2">Kapsam:</span>
    <button
      type="button"
      onClick={() => setScope('page')}
      data-testid={`${idPrefix}-scope-page`}
      className={`px-3 py-1 rounded text-xs ${scope === 'page' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
    >
      Görünen Sayfa ({page})
    </button>
    <button
      type="button"
      onClick={() => setScope('filtered')}
      data-testid={`${idPrefix}-scope-filtered`}
      className={`px-3 py-1 rounded text-xs ${scope === 'filtered' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
    >
      Tüm Filtrelenmiş ({total})
    </button>
  </div>
);

export default DogrudenTeminPage;
